import type { AgentsSurface } from '@lib/analytics/agentsExit';
import { downloadFile } from '@lib/files/download';
import { openAuthedChatUrl } from '@lib/network/chats';
import { getPodcastCreateUrl } from '@lib/network/podcasts';
import { askAgentAboutMedia } from '@lib/starzero-agents';
import { showToast } from '@lib/utils/toastmaster';
import { formatClockLabel } from '@lib/utils/units';
import { copyAppLink, formatMediaTime, getMediaUrl } from '@lib/utils/url';
import { isHostname } from '@lib/utils/youtube';
import { IMAGE_CANNOT_CLIP_REASON, VERBS, type MediaVerbId } from '@lib/verbs';
import { isProbablyImage, mediaVerbUnavailableReason } from '@pages/Library/utils/mediaStatus';
import { debugStore } from '@stores/Debug';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import { Media } from '@typings/Media';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MediaActionControl, MediaMenuAction, MediaMenuActionItem } from './mediaActions';
import { resolveOriginalMediaUrl } from './originalMedia';

/**
 * The pure half of this module's vocabulary — ids, item shape, control names, menu order — lives
 * in `./mediaActions`, so `mediaActionBar.ts` and its unit test can read it without dragging
 * `@lib/verbs`' glyphs (and `@pages`, and their `.module.scss`) in behind them. That isn't
 * hypothetical: importing these types from here made `npx tsx` try to parse a stylesheet.
 *
 * Re-exported so the hook stays the single import most callers need.
 */
export {
  byMediaActionOrder,
  MEDIA_ACTION_ORDER,
  type MediaActionControl,
  type MediaMenuAction,
  type MediaMenuActionItem,
} from './mediaActions';

/**
 * Podcast Clipper lives entirely in the agents app, and `getPodcastCreateUrl`
 * degrades to `''` without `CHAT_URL` — so the action is omitted rather than
 * offered as a dead link. (The agent actions don't need this gate:
 * `starzero-agents.ts` carries its own origin fallback.)
 */
const CHAT_CONFIGURED = !!process.env.CHAT_URL;

/**
 * The half of the media action vocabulary that needs NO library-page context.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS SPLIT IS LOAD-BEARING, NOT TIDINESS. DO NOT MERGE IT BACK.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `useMediaAdminActions` — the other half — calls `useLibraryFolder()` and
 * `useMediaDeleteModal()`. `MediaDeleteModalProvider` is mounted in exactly one place
 * (`LibraryPage.tsx`) and `LibraryFolderProvider` takes one concrete library, so neither can be
 * hoisted app-wide: `/search` spans many libraries and `/atlas` has none in scope. Hooks cannot
 * be called conditionally, so before this split any component that wanted a media menu on
 * `/search` white-screened the route.
 *
 * Everything here reaches only global singletons (`libraryManager`, `userStore`,
 * `trackingStore`) or module-level functions, so it is safe on ANY route. That is what lets the
 * moment modal offer the same CTA and kebab on the library page, on `/search` and in Story
 * Atlas from one component.
 *
 * Keep it that way. Adding a provider-dependent hook to this file re-breaks `/search`, and it
 * will not show up in `tsc` — only at runtime, on a route you may not be testing.
 */
export function useMediaVerbs(
  media: Media,
  {
    playheadTime,
    surface = 'library',
  }: {
    /**
     * Playhead position in seconds, FROZEN by the caller when it opened its menu — not a live
     * read. Supplying it is what offers `copy-link-at-time` at all: most surfaces that mount
     * this hook (grid card, table row, mobile list) show a thumbnail and have no playhead, so
     * there would be no timestamp to put in the link.
     *
     * It must not track the playhead. The label names the timestamp ("Copy link at 1:30"), so
     * a live value would relabel the row under the user's cursor several times a second AND
     * make the label a promise the click can't keep — the video plays on behind the menu, so by
     * the time you click, a live read is a different number than the one you read. One frozen
     * value backs both the label and the link, which is the only way they can agree.
     * `MediaActionBar` and `VideoMenuButton` do the freezing; see `onMenuOpen` /
     * `getCurrentTime`.
     */
    playheadTime?: number;
    /**
     * Which area of the app the handoff leaves from, for `agentsExit`.
     *
     * Defaults to `'library'` because that is where every caller lived until the moment modal's
     * action bar reached `/search` and `/atlas`. Those two must pass their own: the value ends
     * up in `Left For Agents`, and folding a search or Atlas exit into the library's numbers is
     * a silently wrong dashboard with no runtime symptom.
     *
     * Distinct from `control` (passed per click) — that names the button, this names the page.
     */
    surface?: AgentsSurface;
  } = {},
) {
  const navigate = useNavigate();
  const libraryManager = useLibraryManager();
  const library = libraryManager.libraryById(media.libraryId);

  const isCompleted = media.status === 'completed';
  const isExternal = media.origin?.source === 'external';
  const isImage = isProbablyImage(media);

  // The one gate for "is there a timestamp worth linking to" — `formatMediaTime` is the same
  // function `getMediaUrl` will apply, so the row can't exist unless the URL will carry a `t`,
  // and the label can't name a second the link doesn't point at. Null covers all of: no
  // playhead supplied, a player still loading (reads 0), and a playhead genuinely at the start.
  // `formatMediaTime` returns the already-floored seconds as a string, so reading the number
  // back off it keeps one source of truth for the rounding.
  const stampedSeconds = formatMediaTime(playheadTime);
  const stampedPlayhead = stampedSeconds === null ? null : Number(stampedSeconds);

  // Why the three verbs are off while the media isn't ready. `mediaVerbUnavailableReason` splits
  // "still coming" from the two terminal states, so an errored item is never told to wait for
  // processing that isn't coming — and every one of them still gets a sentence.
  const unavailableReason = (id: MediaVerbId) => mediaVerbUnavailableReason(VERBS[id].label, media);

  const actions: MediaMenuActionItem[] = [
    // The three verb labels come from `@lib/verbs`; only the gates below are this file's.
    {
      id: 'work-with-agent',
      label: VERBS['work-with-agent'].label,
      disabled: !isCompleted,
      disabledReason: unavailableReason('work-with-agent'),
    },
    {
      id: 'find-moments',
      label: VERBS['find-moments'].label,
      disabled: !isCompleted,
      disabledReason: unavailableReason('find-moments'),
    },
    // Same `isCompleted` gate as the two verbs above, and deliberately no *duration*
    // threshold: clipping is only meaningful on longer media, but a threshold would
    // make the action vanish on most of the demo library (3–12 min), which is exactly
    // where people are exploring. The clipper rejects unsuitable lengths on its own
    // side.
    //
    // Images are offered but INERT, not omitted. A single frame has no audio to clip, but a
    // control that silently disappears on some media reads as a bug — the user notices the
    // absence, can't tell whether it's broken or deliberate, and has nothing to ask. One
    // dimmed button with a reason answers it. Audio stays fully enabled: podcasts are the
    // case this exists for.
    //
    // `CHAT_CONFIGURED` still omits it entirely, because that isn't a fact about this media —
    // the clipper doesn't exist in that build at all, and explaining a missing env var to a
    // user would be worse than the silence.
    ...(CHAT_CONFIGURED
      ? [
          {
            id: 'clip-podcast' as const,
            label: VERBS['clip-podcast'].label,
            disabled: !isCompleted || isImage,
            // Being an image wins over the wait: an image will finish processing and still have
            // no audio, so telling it to wait would be a promise nothing keeps.
            disabledReason: isImage ? IMAGE_CANNOT_CLIP_REASON : unavailableReason('clip-podcast'),
          },
        ]
      : []),
    { id: 'copy-link', label: 'Copy link', disabled: false },
    // Debuggers only, and deliberately so. It used to be a standalone icon button in the moment
    // modal's header, gated on the search-result variant — which is why it kept "disappearing"
    // on the library page: that variant never rendered it. As a menu row it costs no header
    // space and both variants get it.
    //
    // A user reporting a problem copies the LINK: `getMediaUrl` builds
    // `/library/<libraryId>-<slug>/media/<mediaId>-<slug>`, so both ids are already in it. This
    // row is for the people who want them as clean JSON, which is not a user story.
    ...(debugStore.respectfulDebugMode
      ? [{ id: 'copy-metadata' as const, label: 'Copy metadata', disabled: false }]
      : []),
    // Offered only where there IS a playhead — see `playheadTime`. Alongside `copy-link`
    // rather than replacing it: a link to the media itself is what you want most of the time,
    // and a timestamp the user can't decline would have to be trimmed off by hand.
    //
    // The label carries the actual time, so the row is its own confirmation of what you're
    // about to copy. That also settles the 0:00 case by construction: `stampedPlayhead` is
    // null there (`formatMediaTime` treats the first frame as no timestamp), the row is
    // absent, and "Copy link" directly above it produces exactly the link this row would
    // have — no duplicate, and no row labelled "Copy link at 0:00".
    //
    // Images are excluded outright (a single frame has no position to point at) rather than
    // shown inert, because unlike the clip verb this one has no counterpart on any other
    // surface, so nobody learns it exists and then finds it missing here.
    ...(stampedPlayhead && !isImage
      ? [
          {
            id: 'copy-link-at-time' as const,
            label: `Copy link at ${formatClockLabel(stampedPlayhead)}`,
            disabled: false,
          },
        ]
      : []),
    ...(isExternal
      ? [{ id: 'open-original' as const, label: 'Open Original', disabled: false }]
      : []),
    { id: 'download', label: 'Download', disabled: false },
  ];

  const executeVerb = useCallback(
    async (action: MediaMenuAction, control: MediaActionControl = 'media-kebab') => {
      // Tracked HERE rather than in `useMediaMenuActions`, so a surface that mounts only this
      // half (the moment modal on /search and /atlas) still reports its clicks. The admin half
      // fires the same event for its own actions.
      trackingStore.mediaMenuAction({
        action,
        mediaId: media._id,
        libraryId: media.libraryId,
        control,
      });

      switch (action) {
        case 'work-with-agent':
          askAgentAboutMedia(media.libraryId, media._id, {
            surface,
            control,
          });
          break;

        case 'find-moments':
          navigate(`/search?libs=${media.libraryId}&media=${media._id}`);
          break;

        case 'clip-podcast': {
          // `_blank` matches `askAgentAboutMedia`: both are media-scoped handoffs
          // fired from a grid, so the user keeps their scroll position here.
          const url = getPodcastCreateUrl(media.libraryId, media._id);
          if (url) {
            void openAuthedChatUrl(url, '_blank', {
              surface,
              control,
              libraryId: media.libraryId,
              mediaId: media._id,
            });
          }
          break;
        }

        case 'copy-link':
        case 'copy-link-at-time': {
          // The same frozen number the label was built from, so the link matches what the row
          // said it would be.
          const t = action === 'copy-link-at-time' ? (stampedPlayhead ?? undefined) : undefined;
          const mediaPath = getMediaUrl(media.libraryId, media._id, {
            libraryName: library?.name,
            mediaName: media.metadata?.name,
            t,
          });
          // The same gate `getMediaUrl` just applied, so the toast can't announce a timestamp
          // the URL doesn't carry. A playhead still at the first frame, or a player that never
          // reported a position, copies a plain link and says the plain thing — rather than
          // promising an `?t=` that was dropped as noise.
          const stamped = formatMediaTime(t);
          if (!(await copyAppLink(mediaPath))) {
            showToast('media-link-copy-failed');
          } else if (stamped) {
            showToast('media-link-copied-at-time', { seconds: Number(stamped) });
          } else {
            showToast('media-link-copied');
          }
          break;
        }

        case 'copy-metadata':
          // Silent, like the header button it replaces. Every other copy row here fires a toast,
          // but this one only exists in debug mode, where the person clicking it is about to
          // paste the result somewhere and will see immediately whether it worked.
          await navigator.clipboard.writeText(
            JSON.stringify({ libraryId: media.libraryId, mediaId: media._id }, null, 2),
          );
          break;

        case 'open-original': {
          const url = (media.origin as any)?.url ?? media.metadata?.filename;
          if (url && /^https?:\/\//i.test(url)) {
            window.open(url, '_blank');
          } else {
            showToast('media-original-url-unavailable');
          }
          break;
        }

        case 'download': {
          const mediaMetadata = media.metadata;
          const mediaSource = media.origin?.source;
          let filename: string | undefined;
          if (mediaMetadata) {
            const fn = mediaMetadata.filename;
            let videoName = mediaMetadata.name;
            const extension = mediaMetadata?.fileMappings?.originalMedia?.split('.').pop();
            if (videoName && extension && videoName.endsWith(`.${extension}`)) {
              videoName = videoName.slice(0, -(extension.length + 1));
            }
            if (videoName && extension) filename = `${videoName}.${extension}`;
            else if (fn && mediaSource === 'external' && extension) filename = `${fn}.${extension}`;
            else if (fn && mediaSource === 'user' && extension) filename = `${fn}`;
          }

          try {
            showToast('media-downloading', { filename });
            await downloadFile(await resolveOriginalMediaUrl(media.libraryId, media._id, filename));
          } catch (error) {
            showToast('media-download-failed', { error });
          }
          break;
        }
      }
    },
    [media, library, navigate, stampedPlayhead, surface],
  );

  return { actions, executeVerb };
}

/** Infer provider type from media origin for the "Open Original" icon */
export function inferProviderType(media: Media): string | undefined {
  const type = ((media.origin as any)?.type as string | undefined)?.toLowerCase();
  if (type) return type;
  const candidateUrl = (media.origin as any)?.url ?? media.metadata?.filename;
  if (!candidateUrl) return undefined;
  try {
    const host = new URL(candidateUrl).hostname.toLowerCase();
    if (host.includes('youtube.com') || host.includes('youtu.be')) return 'youtube';
    if (host.includes('vimeo.com')) return 'vimeo';
    if (host.includes('linkedin.com')) return 'linkedin';
    if (host.includes('drive.google.com') || host.includes('google.com')) return 'google-drive';
    if (host.includes('dropbox.com') || host.includes('dropboxusercontent.com')) return 'dropbox';
    if (host.includes('facebook.com')) return 'facebook';
    if (isHostname(host, 'box.com') || isHostname(host, 'boxcloud.com')) return 'box';
    if (host.includes('tiktok.com')) return 'tiktok';
    if (host.includes('soundcloud.com')) return 'soundcloud';
  } catch {}
  return undefined;
}
